import React from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  useSortable,
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Handle } from "@/components/ui/auto-form/common/handle";
import { cn } from "@/lib/utils";

export function SortableList(props: {
  items: any;
  onChanged: (items: any) => void;
  render: (item: any, idx?: number) => React.ReactNode;
  canDelete?: boolean;
  orientation?: "horizontal" | "vertical";
  handle?: boolean;
  className?: string;
  parentClassName?: string;
}) {
  const orientation = props.orientation ?? "horizontal";
  const handle = props.handle ?? false;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (active.id !== over?.id) {
      // Move the item in the list
      const oldIndex = props.items.findIndex((i: any) => i.id === active.id);
      const newIndex = props.items.findIndex((i: any) => i.id === over?.id);

      props.onChanged(arrayMove(props.items, oldIndex, newIndex));
    }
  }

  function handleDeleted(id: string) {
    props.onChanged(props.items.filter((i: any) => i.id !== id));
  }

  const SortableItem =
    orientation === "horizontal"
      ? HorizontalSortableItem
      : VerticalSortableItem;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={props.items}
        strategy={
          orientation === "horizontal"
            ? horizontalListSortingStrategy
            : verticalListSortingStrategy
        }
      >
        <div
          className={
            orientation === "horizontal"
              ? cn("flex flex-row flex-wrap", props.parentClassName)
              : cn("flex flex-col w-full", props.parentClassName)
          }
        >
          {props.items.map((item: any, idx: number) => (
            <SortableItem
              className={props.className}
              key={idx}
              id={item.id}
              onDelete={props.canDelete ? () => handleDeleted(item.id) : null}
              handle={handle}
            >
              {props.render(item, idx)}
            </SortableItem>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

export function VerticalSortableItem(props: any) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    setActivatorNodeRef,
  } = useSortable({ id: props.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div className={cn("relative w-full p-1", props.className ?? "")}>
      {props.onDelete && (
        <Button
          className="absolute top-0 right-0 mr-[-1.2rem] mt-[-1.2rem] p-2 z-10"
          size="icon"
          type="button"
          variant="secondary"
          onClick={props.onDelete}
        >
          <Trash2 />
        </Button>
      )}
      <div
        ref={setNodeRef}
        className={props.className}
        style={style}
        {...attributes}
      >
        {/* ... */}
        {!props.handle ? (
          props.children
        ) : (
          <div className="flex flex-row space-x-2">
            {props.children}

            <Handle ref={setActivatorNodeRef} {...listeners} />
          </div>
        )}
        {/* {props.children} */}
      </div>
    </div>
  );
}

export function HorizontalSortableItem(props: any) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: props.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div className="relative p-1">
      {props.onDelete && (
        <Button
          className="absolute top-0 right-0 mr-[-1.2rem] mt-[-1.2rem] p-2 z-10"
          size="icon"
          type="button"
          variant="secondary"
          onClick={props.onDelete}
        >
          <Trash2 />
        </Button>
      )}
      <div
        ref={setNodeRef}
        className={props.className}
        style={style}
        {...attributes}
        {...listeners}
      >
        {/* ... */}
        {props.children}
      </div>
    </div>
  );
}
